import { CertificationConfig } from '../types';

export const DEFAULT_CERTIFICATIONS: CertificationConfig[] = [
  {
    id: 'saglik-bakanligi',
    title: 'T.C. Sağlık Bakanlığı',
    badgeText: 'Klinik Ruhsatı',
    description: 'Mevzuata Uygun Klinik Denetimi',
    subCode: '',
    customImage: '/uploads/certifications/cert-saglik-bakanligi-1788465264618.png',
    enabled: true,
  },
  {
    id: 'ce-medical',
    title: 'CE Medikal Uygunluk',
    badgeText: 'CE 0197 Onayı',
    description: 'Avrupa Tıbbi Cihaz Standardı',
    subCode: '',
    customImage: '/uploads/certifications/cert-ce-medical-1788465270428.png',
    enabled: true,
  },
  {
    id: 'tuv-certified',
    title: 'TÜV Sertifikasyonu',
    badgeText: 'TÜV / SÜD Onayı',
    description: 'Uluslararası Güvenlik & Kalite',
    subCode: 'No : G1 002325',
    customImage: '/uploads/certifications/cert-tuv-certified-1788465288123.svg',
    enabled: true,
  },
  {
    id: 'mdr-regulation',
    title: 'EU MDR Regülasyonu',
    badgeText: 'EU 2017/745 MDR',
    description: 'Tıbbi Cihaz Mevzuatı Uyumu',
    subCode: 'Class IIa Medical Device',
    customImage: '/uploads/certifications/cert-mdr-regulation-1788465298096.png',
    enabled: true,
  },
  {
    id: 'emc-laser',
    title: 'Ürün Takip Sistemi',
    badgeText: 'Sağlık Bakanlığı',
    description: 'Mevzuata Uygun Cihaz Kaydı',
    subCode: '',
    customImage: '/uploads/certifications/cert-emc-laser-1788465313344.png',
    enabled: true,
  },
];

const LOCAL_STORAGE_KEY = 'bursa_certifications_config';
const CERTIFICATIONS_UPDATE_EVENT = 'bursa_certifications_updated';

// Get current certifications (localStorage with default fallback)
export function getStoredCertifications(): CertificationConfig[] {
  if (typeof window === 'undefined') return DEFAULT_CERTIFICATIONS;
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const parsed: CertificationConfig[] = JSON.parse(raw);
      // Merge with default list in case new ones were added or images updated
      return DEFAULT_CERTIFICATIONS.map((def) => {
        const found = parsed.find((p) => p.id === def.id);
        if (!found) return def;
        return {
          ...def,
          ...found,
          customImage: found.customImage || def.customImage,
        };
      });
    }
  } catch (e) {
    console.warn('[Certifications] Read error:', e);
  }
  return DEFAULT_CERTIFICATIONS;
}

// Save locally and trigger event
export function saveStoredCertifications(certs: CertificationConfig[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(certs));
    window.dispatchEvent(new CustomEvent(CERTIFICATIONS_UPDATE_EVENT, { detail: certs }));
  } catch (e) {
    console.warn('[Certifications] Save error:', e);
  }
}

// Subscribe to live certification changes across components
export function subscribeToCertifications(callback: (certs: CertificationConfig[]) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (e: Event) => {
    const customEvt = e as CustomEvent<CertificationConfig[]>;
    if (customEvt.detail) {
      callback(customEvt.detail);
    } else {
      callback(getStoredCertifications());
    }
  };

  window.addEventListener(CERTIFICATIONS_UPDATE_EVENT, handler);
  window.addEventListener('storage', (e) => {
    if (e.key === LOCAL_STORAGE_KEY) {
      callback(getStoredCertifications());
    }
  });

  return () => {
    window.removeEventListener(CERTIFICATIONS_UPDATE_EVENT, handler);
  };
}

// Fetch from backend server and sync local cache
export async function syncCertificationsWithServer(): Promise<CertificationConfig[]> {
  try {
    const res = await fetch(`/api/certifications?_t=${Date.now()}`);
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.certifications)) {
        saveStoredCertifications(data.certifications);
        return data.certifications;
      }
    }
  } catch (err) {
    console.warn('[Certifications] Server sync notice:', err);
  }
  return getStoredCertifications();
}
